import { faX, faY } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import { type CreateParkingGatewayFormSchemaValues } from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useCreateParkingGateway.ts';
import { GATEWAY } from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { FormFieldBlock, FormFieldBlockBody } from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithIcon } from 'src/registry/components/inputs/InputWithIcon';
import {
    Form,
    FormControl,
    FormControlStack,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form.tsx';
import { Input } from 'src/registry/ui/Input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select.tsx';
import { generalMessages } from 'src/utils/messages.ts';

import type { UseFormReturn } from 'react-hook-form';

interface ParkingGatewayFormProps {
    form: UseFormReturn<CreateParkingGatewayFormSchemaValues>;
    onSubmit: () => void;
}

const ParkingGatewayForm = ({ form, onSubmit }: ParkingGatewayFormProps) => {
    const { formatMessage } = useIntl();

    const SelectParkingGateway = {
        [GATEWAY.ENTRY]: parkingManagementMessages.entrance,
        [GATEWAY.EXIT]: parkingManagementMessages.exit,
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.name)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Input type="text" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="gateway_type"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(parkingManagementMessages.type)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Select value={field.value || ''} onValueChange={field.onChange}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(parkingManagementMessages.selectType)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(SelectParkingGateway).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {formatMessage(value)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.address)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Input type="text" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormFieldBlock>
                <FormLabel variant="dialog" required>
                    {formatMessage(parkingManagementMessages.coordinates)}
                </FormLabel>
                <FormFieldBlockBody className="flex-1">
                    <div className="flex flex-1 flex-col gap-2 md:mr-4 md:flex-row">
                        <FormField
                            control={form.control}
                            name="longitude"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            icon={faX}
                                            inputMode="decimal"
                                            type="text"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="latitude"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputWithIcon
                                            icon={faY}
                                            inputMode="decimal"
                                            type="text"
                                            pattern="^-?\d*(\.?\d{0,16})?$"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormField
                control={form.control}
                name="max_height_cm"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog">
                            {`${formatMessage(parkingManagementMessages.maxHeight)} ${formatMessage(parkingManagementMessages.inCm)}`}
                        </FormLabel>
                        <FormControl>
                            <Input
                                type="number"
                                min={0}
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    if (value === '' || Number(value) >= 0) {
                                        field.onChange(value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === '-' || e.key === '+' || e.key === 'e') {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="max_width_cm"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog">
                            {`${formatMessage(parkingManagementMessages.maxWidth)} ${formatMessage(parkingManagementMessages.inCm)}`}
                        </FormLabel>
                        <FormControl>
                            <Input
                                type="number"
                                min={0}
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    if (value === '' || Number(value) >= 0) {
                                        field.onChange(value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === '-' || e.key === '+' || e.key === 'e') {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </Form>
    );
};

export default ParkingGatewayForm;
